/**
 * DecisionSupportPanel Component
 * Guides users to actions, not just observations
 * Provides actionable recommendations with clear impact analysis
 */

import { useState } from 'react'
import {
  Compass,
  CheckCircle2,
  Circle,
  ArrowRight,
  Clock,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Target,
  Gauge,
  DollarSign,
  Shield
} from 'lucide-react'
import clsx from 'clsx'

interface ActionStep {
  id: string
  title: string
  description: string
  completed?: boolean
  duration?: string
}

interface ActionRecommendation {
  id: string
  title: string
  description: string
  impact: {
    type: 'reliability' | 'cost' | 'safety' | 'performance'
    value: string
    improvement: number
  }
  effort: {
    time: string
    complexity: 'low' | 'medium' | 'high'
  }
  priority: 'critical' | 'high' | 'medium' | 'low'
  steps?: ActionStep[]
  assetId?: string
  deadline?: string
  onClick?: () => void
}

interface DecisionSupportPanelProps {
  title?: string
  subtitle?: string
  recommendations: ActionRecommendation[]
  onActionStart?: (id: string) => void
  onStepComplete?: (actionId: string, stepId: string) => void
  showImpactSummary?: boolean
  className?: string
}

export default function DecisionSupportPanel({
  title = 'Recommended Actions',
  subtitle,
  recommendations,
  onActionStart,
  onStepComplete,
  showImpactSummary = true,
  className
}: DecisionSupportPanelProps) {
  const [expandedAction, setExpandedAction] = useState<string | null>(
    recommendations.find(r => r.priority === 'critical')?.id || null
  )
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())

  const handleStepToggle = (actionId: string, stepId: string) => {
    const key = `${actionId}-${stepId}`
    setCompletedSteps(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
        onStepComplete?.(actionId, stepId)
      }
      return next
    })
  }

  const getImpactIcon = (type: ActionRecommendation['impact']['type']) => {
    switch (type) {
      case 'reliability':
        return <Gauge size={14} />
      case 'cost':
        return <DollarSign size={14} />
      case 'safety':
        return <Shield size={14} />
      case 'performance':
        return <TrendingUp size={14} />
      default:
        return <Target size={14} />
    }
  }

  const getPriorityBadge = (priority: ActionRecommendation['priority']) => {
    switch (priority) {
      case 'critical':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-status-critical/20 text-status-critical border border-status-critical/30">
            Critical
          </span>
        )
      case 'high':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-status-warning/20 text-status-warning border border-status-warning/30">
            High Priority
          </span>
        )
      case 'medium':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500/20 text-primary-400 border border-primary-500/30">
            Suggested
          </span>
        )
      default:
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-surface-700 text-surface-400 border border-surface-600">
            Optional
          </span>
        )
    }
  }

  const getComplexityColor = (complexity: 'low' | 'medium' | 'high') => {
    switch (complexity) {
      case 'low':
        return 'text-status-healthy'
      case 'medium':
        return 'text-status-warning'
      case 'high':
        return 'text-status-critical'
    }
  }

  // Calculate total potential impact
  const totalImpact = recommendations.reduce((sum, r) => sum + r.impact.improvement, 0)
  const criticalCount = recommendations.filter(r => r.priority === 'critical').length

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-primary-500/10 to-transparent border-b border-surface-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-500/20 text-primary-400">
              <Compass size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-surface-100">{title}</h3>
              {subtitle && <p className="text-xs text-surface-400 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {criticalCount > 0 && (
            <div className="flex items-center gap-1 text-status-critical">
              <AlertTriangle size={14} className="animate-pulse" />
              <span className="text-xs font-medium">{criticalCount} require immediate attention</span>
            </div>
          )}
        </div>

        {/* Impact Summary */}
        {showImpactSummary && (
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-surface-700/50">
            <div className="text-center">
              <p className="text-2xl font-bold text-status-healthy">+{totalImpact}%</p>
              <p className="text-xs text-surface-400">Potential Improvement</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-primary-400">{recommendations.length}</p>
              <p className="text-xs text-surface-400">Actions Available</p>
            </div>
            <div className="flex-1">
              <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-status-healthy to-primary-500 rounded-full transition-all"
                  style={{ width: `${Math.min(totalImpact, 100)}%` }}
                />
              </div>
              <p className="text-xs text-surface-400 mt-1 text-right">Impact potential</p>
            </div>
          </div>
        )}
      </div>

      {/* Recommendations List */}
      <div className="divide-y divide-surface-800">
        {recommendations.map((rec) => {
          const isExpanded = expandedAction === rec.id
          const stepsCompleted = rec.steps?.filter(s =>
            completedSteps.has(`${rec.id}-${s.id}`) || s.completed
          ).length || 0
          const totalSteps = rec.steps?.length || 0
          const progress = totalSteps > 0 ? (stepsCompleted / totalSteps) * 100 : 0

          return (
            <div key={rec.id} className="overflow-hidden">
              {/* Recommendation Header */}
              <button
                onClick={() => setExpandedAction(isExpanded ? null : rec.id)}
                className={clsx(
                  'w-full p-4 text-left transition-colors',
                  rec.priority === 'critical' && 'bg-status-critical/5',
                  'hover:bg-surface-800/50'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Priority Indicator */}
                  <div className={clsx(
                    'mt-1 w-1.5 h-1.5 rounded-full',
                    rec.priority === 'critical' && 'bg-status-critical animate-pulse',
                    rec.priority === 'high' && 'bg-status-warning',
                    rec.priority === 'medium' && 'bg-primary-400',
                    rec.priority === 'low' && 'bg-surface-500'
                  )} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-surface-200">{rec.title}</h4>
                      {getPriorityBadge(rec.priority)}
                      {rec.assetId && (
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-primary-500/10 text-primary-400">
                          {rec.assetId}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-surface-400 mt-1">{rec.description}</p>

                    {/* Quick Stats */}
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className={clsx('flex items-center gap-1', 'text-status-healthy')}>
                        {getImpactIcon(rec.impact.type)}
                        <span className="text-surface-400">{rec.impact.value}</span>
                        <span className="font-medium">+{rec.impact.improvement}%</span>
                      </span>
                      <span className="flex items-center gap-1 text-surface-400">
                        <Clock size={12} />
                        {rec.effort.time}
                      </span>
                      <span className={clsx('flex items-center gap-1', getComplexityColor(rec.effort.complexity))}>
                        <Lightbulb size={12} />
                        {rec.effort.complexity} effort
                      </span>
                      {rec.deadline && (
                        <span className="flex items-center gap-1 text-status-warning">
                          <AlertTriangle size={12} />
                          Due: {rec.deadline}
                        </span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {totalSteps > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-surface-400">{stepsCompleted} of {totalSteps} steps</span>
                          <span className="text-surface-400">{Math.round(progress)}%</span>
                        </div>
                        <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-surface-500">
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>
              </button>

              {/* Expanded Steps */}
              {isExpanded && rec.steps && rec.steps.length > 0 && (
                <div className="px-4 pb-4 bg-surface-800/30">
                  <div className="ml-4 pl-4 border-l-2 border-surface-700 space-y-3">
                    {rec.steps.map((step, index) => {
                      const isCompleted = completedSteps.has(`${rec.id}-${step.id}`) || step.completed

                      return (
                        <div
                          key={step.id}
                          className={clsx(
                            'flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer',
                            isCompleted ? 'bg-status-healthy/5' : 'bg-surface-800/50 hover:bg-surface-800'
                          )}
                          onClick={() => handleStepToggle(rec.id, step.id)}
                        >
                          <div className={clsx(
                            'mt-0.5 transition-colors',
                            isCompleted ? 'text-status-healthy' : 'text-surface-500'
                          )}>
                            {isCompleted ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-surface-500 font-mono">Step {index + 1}</span>
                              {step.duration && (
                                <span className="text-xs text-surface-500 flex items-center gap-1">
                                  <Clock size={10} />
                                  {step.duration}
                                </span>
                              )}
                            </div>
                            <h5 className={clsx(
                              'font-medium text-sm mt-1',
                              isCompleted ? 'text-surface-400 line-through' : 'text-surface-200'
                            )}>
                              {step.title}
                            </h5>
                            <p className="text-xs text-surface-400 mt-0.5">{step.description}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Start Action Button */}
                  <div className="mt-4 ml-4">
                    <button
                      onClick={() => {
                        onActionStart?.(rec.id)
                        rec.onClick?.()
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Start This Action
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
